import { type FormEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '@assets/icons/safe.svg?react';
import { Eye, EyeOff } from 'lucide-react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@ui/button';
import { Input } from '@ui/input';
import { routesMap } from '@shared/lib';
import { z } from 'zod';

import { confirmPasswordReset } from '../../../services';

import styles from './confirm.module.css';

const PASSWORD_RULE_MESSAGE =
  'Must contain 1 uppercase letter, 1 number, min. 8 characters.';

const confirmSchema = z.object({
  password: z
    .string()
    .min(1, 'Enter a new password')
    .min(8, PASSWORD_RULE_MESSAGE)
    .regex(/[A-Z]/, PASSWORD_RULE_MESSAGE)
    .regex(/\d/, PASSWORD_RULE_MESSAGE),
});

type ConfirmFormValues = z.infer<typeof confirmSchema>;

type ConfirmProps = {
  token: string | null;
};

export const Confirm = ({ token }: ConfirmProps) => {
  const navigate = useNavigate();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [serviceError, setServiceError] = useState('');
  const { mutateAsync: confirm, isPending } = useMutation({
    mutationFn: confirmPasswordReset,
  });
  const form = useForm<ConfirmFormValues>({
    resolver: zodResolver(confirmSchema),
    reValidateMode: 'onSubmit',
    shouldFocusError: true,
    defaultValues: {
      password: '',
    },
  });

  const submitPasswordConfirm = async ({ password }: ConfirmFormValues) => {
    setServiceError('');

    if (!token) {
      setServiceError('Reset token is missing');
      return;
    }

    const response = await confirm({ token, newPassword: password });

    if (!response.success) {
      setServiceError(response.error || 'Failed to reset password');
      return;
    }

    form.reset();
    navigate(routesMap.auth, { replace: true });
  };

  const error = form.formState.errors.password?.message || serviceError;
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (!token) {
      event.preventDefault();
      form.clearErrors();
      setServiceError('Reset token is missing');
      return;
    }

    void form.handleSubmit(submitPasswordConfirm, () => setServiceError(''))(
      event,
    );
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.header}>
        <h1 className={styles.title}>Set new</h1>
      </div>
      <div className={styles.fields}>
        <label>
          <span className={styles.label}>Password</span>
          <span className={styles.field}>
            <SafeIcon className={styles.icon} aria-hidden />
            <Input
              className={styles.input}
              type={isPasswordVisible ? 'text' : 'password'}
              placeholder="Enter password"
              autoComplete="new-password"
              inputSize="lg"
              {...form.register('password')}
            />
            <button
              className={styles.passwordToggle}
              type="button"
              onClick={() => setIsPasswordVisible((value) => !value)}
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
            >
              {isPasswordVisible ? (
                <EyeOff className={styles.passwordIcon} />
              ) : (
                <Eye className={styles.passwordIcon} />
              )}
            </button>
          </span>
        </label>
        <p className={styles.hint}>
          Must contain 1 uppercase letter, 1 number, min. 8 characters.
        </p>
        <div className={styles.strength} aria-hidden>
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actions}>
        <Button className={styles.button} type="submit" disabled={isPending}>
          It Ready!
        </Button>
      </div>
    </form>
  );
};
